import { TodoFolder } from './TodoFolder'
import { TodoItem } from './TodoItem'

import { getTodoDB, isTodoDBEnabled, isTodoDBReady } from './todoDB'
import { getSortedFolders, resolveSyncFolderUrls } from './TodoFolderSync'

/**
 * @typedef {Object} TodoUserInfo
 * @property {string=} name
 * @property {string=} email
 */
export interface TodoUserInfo {
  name?: string
  email?: string
}

/**
 * @typedef {Object} AccountWithTodoFolders
 * @property {TodoUserInfo} userInfo
 * @property {import('./TodoFolder').TodoFolder[]} folders
 */
export interface AccountWithTodoFolders {
  userInfo: TodoUserInfo
  folders: TodoFolder[]
}

// 전역 공유 상태 - TodoService, TodoSync, TodoFolderSync에서 공통으로 사용
const accountWithFolders: AccountWithTodoFolders = {
  userInfo: {} as TodoUserInfo,
  folders: [],
}

export const getSharedAccountWithFolders = (): AccountWithTodoFolders => {
  return accountWithFolders
}

// 변경 사항 있는지 확인하기 위해.
export const deepEqual = (a: any, b: any): boolean => {
  if (a === b) return true
  if (a == null || b == null) return false
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) return false
    }
    return true
  }
  if (typeof a === 'object' && typeof b === 'object') {
    const keysA = Object.keys(a)
    const keysB = Object.keys(b)
    if (keysA.length !== keysB.length) return false
    for (let key of keysA) {
      if (!keysB.includes(key) || !deepEqual(a[key], b[key])) return false
    }
    return true
  }
  return false
}
// 순환 참조 방지: TodoFilter, TodoFolderManager, TodoFolderSync, TodoManager, TodoSync는
// 순환 의존성을 유발하므로 re-export 제거. 소비자는 각 모듈에서 직접 import.
// TodoService → TodoFilter → TodoDBService → TodoService 순환 경로 차단.

// const DEBUG = false

/**
 * collection serverId -> todoItems 캐시용으로 사용 - 추후 DB 랑 연결하자.
 */
const todoItems: Record<string, TodoItem[]> = {}

/**
 * Todo 항목 관리 함수들 추가
 *   - 메모리 캐시 업데이트
 *   - DB 저장 (isTodoDBEnabled && isTodoDBReady)
 *   - await invalidateCache() 호출로 필터 캐시 무효화
 *
 *  UI 업데이트 트리거
 *   - todoItemEmitter.emit('item') → useTodoList.loadTodos() 호출
 *   - DB 또는 메모리 캐시에서 최신 데이터 조회하여 UI 갱신
 */
export const updateTodoItems = async (
  folderUrl: string,
  items: TodoItem[] | any,
): Promise<boolean> => {
  const newItems: TodoItem[] = Array.isArray(items) ? items : Object.values(items)
  const existingItems = todoItems[folderUrl] || []

  // 변경사항 감지
  const hasChanges = !deepEqual(existingItems, newItems)

  // 변경사항이 있을 때만 업데이트
  if (hasChanges) {
    todoItems[folderUrl] = newItems

    // DB 연동이 활성화된 경우 DB에도 저장
    if (isTodoDBEnabled() && isTodoDBReady()) {
      try {
        const todoDB = getTodoDB()

        // 폴더의 기존 Todo 삭제 후 새로운 Todo들 저장
        const existingTodos = await todoDB.getTodosByFolder(folderUrl)
        for (const todo of existingTodos) {
          await todoDB.deleteTodo(todo.url)
        }

        // 새로운 Todo들 저장
        if (newItems.length > 0) {
          await todoDB.saveTodos(newItems)
        }

        console.log(`Updated ${newItems.length} todos in DB for folder: ${folderUrl}`)
      } catch (error) {
        console.error('Failed to update todos in DB:', error)
      }
    }
  }

  return hasChanges
}

export const removeTodoItems = async (folderUrl: string): Promise<void> => {
  delete todoItems[folderUrl]

  // DB 연동이 활성화된 경우 DB에서도 삭제
  if (isTodoDBEnabled() && isTodoDBReady()) {
    try {
      const todoDB = getTodoDB()
      const existingTodos = await todoDB.getTodosByFolder(folderUrl)

      for (const todo of existingTodos) {
        await todoDB.deleteTodo(todo.url)
      }

      console.log(`Removed todos from DB for folder: ${folderUrl}`)
    } catch (error) {
      console.error('Failed to remove todos from DB:', error)
    }
  }
}

export const getTodoItems = async (folderUrl: string): Promise<TodoItem[]> => {
  // DB 연동이 활성화된 경우 DB에서 조회
  if (isTodoDBEnabled() && isTodoDBReady()) {
    try {
      const todoDB = getTodoDB()

      if (folderUrl) {
        return await todoDB.getTodosByFolder(folderUrl)
      } else {
        // 모든 동기화 폴더의 todo 반환
        return await todoDB.getSyncFolderTodos()
      }
    } catch (error) {
      console.error('Failed to get todos from DB:', error)
      // DB 조회 실패 시 메모리 캐시 사용
    }
  }

  // 메모리 캐시에서 조회 (기존 로직)
  if (folderUrl) {
    return todoItems[folderUrl] || []
  }
  // 모든 폴더의 todo를 배열로 반환
  return Object.values(todoItems).flat()
}

/**
 * 동기화된 폴더들의 모든 todo 항목 가져오기 (캐시 최적화)
 *  Sync 후 사용자가 보는 정보 업데이트 흐름:
 *   1. 서버 sync → etag 기반 변경 감지
 *   2. 메모리 캐시 업데이트 → 즉시 반영
 *   3. DB 업데이트 → 서버 데이터로 전체 교체
 *   4. todoItemEmitter.emit('item') → UI 컴포넌트에 알림
 *   5. useTodoList.loadTodos() → DB 또는 메모리 캐시에서 최신 데이터 조회
 *   6. setTodoList(filteredTodos) → 사용자 화면 갱신
 */
export const getSyncTodoItems = async (): Promise<TodoItem[]> => {
  console.log('[getSyncTodoItems] Starting to get sync todos')

  // Sync 대상 폴더의 절대 URL 목록 조회 (settings 는 path 로 저장돼 있으므로 역매핑한다)
  const syncTargets = resolveSyncFolderUrls()
  if (!syncTargets || syncTargets.length === 0) {
    console.log('[getSyncTodoItems] No sync targets found')
    return []
  }

  console.log(`[getSyncTodoItems] Sync targets: ${syncTargets}`)

  // DB 우선 사용 (캐시 제거)
  if (isTodoDBEnabled() && isTodoDBReady()) {
    try {
      const todoDB = getTodoDB()
      const result = await todoDB.getSyncFolderTodos()
      console.log(`[getSyncTodoItems] DB returned ${result.length} todos`)
      return result
    } catch (error) {
      console.error('[getSyncTodoItems] DB error, falling back to memory:', error)
    }
  }

  // Fallback: 메모리에서 구성
  console.log('[getSyncTodoItems] Using memory composition logic')
  const allTodos: TodoItem[] = []

  for (const folderUrl of syncTargets) {
    const folderTodos = todoItems[folderUrl] || []
    console.log(`[getSyncTodoItems] Folder ${folderUrl}: ${folderTodos.length} todos`)
    allTodos.push(...folderTodos)
  }

  console.log(`[getSyncTodoItems] Composed ${allTodos.length} todos from memory`)
  return allTodos
}

/**
 * DB 기반 필터링된 Todo 조회
 */
export const getFilteredTodosFromDB = async (
  filterType: string,
  focusDate: Date | null,
  folderUrls?: string[],
): Promise<TodoItem[]> => {
  if (!isTodoDBEnabled() || !isTodoDBReady()) {
    console.log('[getFilteredTodosFromDB] DB not ready, returning empty array')
    return []
  }

  try {
    const todoDB = getTodoDB()

    // 동기화 폴더 정보 로깅
    if (!folderUrls) {
      const syncTargets = resolveSyncFolderUrls()
      console.log(
        `[getFilteredTodosFromDB] No folderUrls provided, using sync targets: ${syncTargets.length} folders`,
      )
      console.log('[getFilteredTodosFromDB] Sync targets:', syncTargets)
    }

    const result = await todoDB.getFilteredTodos(filterType, folderUrls, focusDate)
    console.log(
      `[getFilteredTodosFromDB] DB returned ${result.length} todos for filter: ${filterType}`,
    )

    return result
  } catch (error) {
    console.error('Failed to get filtered todos from DB:', error)
    return []
  }
}

/**
 * DB 기반 필터 카운트 조회
 */
export const getFilterCountsFromDB = async (
  folderUrls?: string[],
): Promise<Record<string, number>> => {
  if (!isTodoDBEnabled() || !isTodoDBReady()) {
    return {
      all: 0,
      today: 0,
      important: 0,
      planned: 0,
      overdue: 0,
      completed: 0,
    }
  }

  try {
    const todoDB = getTodoDB()
    return await todoDB.getFilterCounts(folderUrls)
  } catch (error) {
    console.error('Failed to get filter counts from DB:', error)
    return {
      all: 0,
      today: 0,
      important: 0,
      planned: 0,
      overdue: 0,
      completed: 0,
    }
  }
}

/**
 * 단일 Todo 저장 (Editor 에서 사용)
 */
export const saveTodo = async (todo: TodoItem): Promise<void> => {
  console.log(
    `[saveTodo] Saving todo: ${todo.url || todo.uid} (status: ${todo.status}, priority: ${todo.priority})`,
  )

  const folderUrl = todo.folderUrl
  if (!folderUrl) {
    console.error('[saveTodo] No folderUrl provided')
    return
  }

  // 원본 상태 백업 (롤백용)
  const existingTodos = todoItems[folderUrl] || []
  const originalTodos = [...existingTodos]

  try {
    // 1단계: 메모리 캐시 업데이트 (임시)
    const existingIndex = todo.url
      ? existingTodos.findIndex((t) => t.url === todo.url)
      : existingTodos.findIndex((t) => t.uid === todo.uid)

    if (existingIndex >= 0) {
      existingTodos[existingIndex] = todo
      console.log(`[saveTodo] Updated existing todo in cache: ${todo.url || todo.uid}`)
    } else {
      existingTodos.push(todo)
      console.log(`[saveTodo] Added new todo to cache: ${todo.url || todo.uid}`)
    }

    todoItems[folderUrl] = existingTodos

    // 2단계: DB 저장 (DB 사용 가능한 경우)
    if (isTodoDBEnabled() && isTodoDBReady()) {
      try {
        const todoDB = getTodoDB()
        const todoForDB = {
          ...todo,
          url: todo.url || `temp-${todo.uid}`,
        }

        await todoDB.saveTodo(todoForDB)
        console.log(`[saveTodo] Saved todo to DB: ${todoForDB.url}`)
      } catch (dbError: any) {
        console.error('[saveTodo] DB save failed, rolling back memory cache:', dbError)

        // DB 저장 실패 시 메모리 롤백
        todoItems[folderUrl] = originalTodos
        throw new Error(`DB save failed: ${dbError.message}`)
      }
    }

    console.log(`[saveTodo] Folder ${folderUrl} now has ${existingTodos.length} todos`)

    // UI 업데이트 이벤트 트리거
    try {
      const { todoItemEmitter } = await import('./TodoSync')
      todoItemEmitter.emit('item')
      console.log('[saveTodo] Todo item update event emitted')
    } catch (error) {
      console.error('[saveTodo] Failed to emit todo item update event:', error)
    }
  } catch (error: any) {
    console.error(`[saveTodo] Failed to save todo: ${error.message}`)
    // 실패 시 원본 상태로 복원
    todoItems[folderUrl] = originalTodos
    throw error
  }
}

/**
 * 단일 Todo 삭제 (Editor 에서 사용)
 */
export const deleteTodo = async (todoUrl: string, folderUrl?: string): Promise<void> => {
  // 메모리 캐시에서 삭제
  if (folderUrl) {
    const existingTodos = todoItems[folderUrl] || []
    // URL이 있는 경우 URL로 삭제, 없는 경우 UID로 삭제 (새 todo의 경우)
    todoItems[folderUrl] = existingTodos.filter((t) => {
      if (todoUrl && todoUrl !== 'null') {
        return t.url !== todoUrl
      } else {
        // todoUrl이 null인 경우 UID로 찾아서 삭제
        return t.uid !== todoUrl
      }
    })
    console.log(`Removed todo from cache: ${todoUrl}`)
  } else {
    // 모든 폴더에서 찾아서 삭제
    Object.keys(todoItems).forEach((fUrl) => {
      todoItems[fUrl] = todoItems[fUrl].filter((t) => {
        if (todoUrl && todoUrl !== 'null') {
          return t.url !== todoUrl
        } else {
          return t.uid !== todoUrl
        }
      })
    })
    console.log(`Removed todo from all folders: ${todoUrl}`)
  }

  // DB 연동이 활성화된 경우 DB에서도 삭제
  if (isTodoDBEnabled() && isTodoDBReady()) {
    try {
      const todoDB = getTodoDB()
      await todoDB.deleteTodo(todoUrl)
      console.log(`Deleted todo from DB: ${todoUrl}`)
    } catch (error) {
      console.error('Failed to delete todo from DB:', error)
    }
  }

  // UI 업데이트 이벤트 트리거 - Todo 항목 삭제 알림
  try {
    const { todoItemEmitter } = await import('./TodoSync')
    todoItemEmitter.emit('item')
    console.log('Todo item delete event emitted')
  } catch (error) {
    console.error('Failed to emit todo item delete event:', error)
  }
}

/**
 * Todo DB 데이터 완전 삭제 함수들
 */

/**
 * 모든 Todo 데이터 삭제
 */
export const clearAllTodos = async (): Promise<boolean> => {
  try {
    // 메모리 캐시 삭제
    Object.keys(todoItems).forEach((key) => {
      delete todoItems[key]
    })
    console.log('Cleared all todos from memory cache')

    // DB에서도 삭제
    if (isTodoDBEnabled() && isTodoDBReady()) {
      const { TodoDBManagementService } = await import('./todoDB/TodoDBService')
      const success = await TodoDBManagementService.clearAllTodos()
      if (success) {
        console.log('Successfully cleared all todos from DB')
      }
      return success
    }

    return true
  } catch (error) {
    console.error('Failed to clear all todos:', error)
    return false
  }
}

/**
 * 전체 TodoDB 데이터 삭제 (폴더 + Todo 모두)
 */
export const clearAllTodoData = async (): Promise<boolean> => {
  try {
    // 메모리 캐시 완전 삭제
    Object.keys(todoItems).forEach((key) => {
      delete todoItems[key]
    })

    // accountWithFolders 초기화
    const accountWithFolders = getSharedAccountWithFolders()
    accountWithFolders.userInfo = {} as TodoUserInfo
    accountWithFolders.folders = []

    console.log('Cleared all todo data from memory')

    // DB 데이터 완전 삭제
    if (isTodoDBEnabled() && isTodoDBReady()) {
      const { TodoDBManagementService } = await import('./todoDB/TodoDBService')
      const success = await TodoDBManagementService.clearAllData()
      if (success) {
        console.log('Successfully cleared all todo data from DB')
      }
      return success
    }

    return true
  } catch (error) {
    console.error('Failed to clear all todo data:', error)
    return false
  }
}

/**
 * 로그아웃 시 데이터 완전 정리
 */
export const logoutAndClearData = async (): Promise<void> => {
  try {
    console.log('Starting logout and data clear process...')

    // 모든 데이터 삭제
    await clearAllTodoData()

    // DB 연결 종료
    const todoDB = getTodoDB()
    todoDB.close()

    console.log('Logout and data clear completed')
  } catch (error) {
    console.error('Error during logout and data clear:', error)
  }
}

// Legacy cache functions removed - IndexedDB handles caching natively
// getCacheStatus 제거 - 사용처 없음
// export const getCacheStatus = () => {
//   return {
//     message: 'Cache system removed - IndexedDB handles caching natively',
//     indexedDBEnabled: isTodoDBEnabled(),
//     indexedDBReady: isTodoDBReady(),
//   }

// }

export const getSortedFoldersProxy = () => {
  return getSortedFolders()
}
